/* ============================================================
   CART DRAWER — assets/bk-cart-drawer.js
   Dépendances : aucune.

   Ce fichier définit l'élément personnalisé <cart-drawer>, celui que le
   reste du thème interroge via document.querySelector("cart-drawer").
   Il expose donc l'API attendue par le thème :

     open(triggeredBy) / close()
     getSectionsToRender()          <- product-form.js
     setActiveElement(element)      <- product-form.js, quick-add.js
     renderContents(parsedState)    <- product-form.js, upsell-simple.js,
                                       shop-cart-button-handler.js
     refreshCartDrawer(sections)    <- bundle-and-save.liquid, upsell-simple.js
                                       (+ window.refreshCartDrawer, cart:ajax-update)

   ⚠️ Volontairement PAS d'interception de submit sur les formulaires
   /cart/add : product-form.js et shop-cart-button-handler.js le font déjà.
   En rajouter une provoquerait un double ajout au panier.

   Alias : window.BkCart = { open, close, refresh, addItem }
   ============================================================ */
(function () {
  'use strict';

  var SECTION_ID = 'cart-drawer';
  var BUBBLE_ID = 'cart-icon-bubble';
  var ROOT_SELECTOR = '#BkCartDrawer';
  var TIMER_STORAGE_KEY = 'bkcart_timer_deadline';

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function root() {
    return document.querySelector(ROOT_SELECTOR);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) throw data;
        return data;
      });
    });
  }

  /* ---------------------------------------------------------
     Compte à rebours
     --------------------------------------------------------- */
  var timerInterval = null;

  function startTimer() {
    var el = root();
    if (!el) return;
    var node = el.querySelector('[data-cart-timer]');
    if (!node) {
      if (timerInterval) clearInterval(timerInterval);
      timerInterval = null;
      return;
    }

    var minutes = parseInt(el.dataset.timerMinutes, 10) || 10;

    var deadline = parseInt(sessionStorage.getItem(TIMER_STORAGE_KEY), 10);
    if (!deadline || isNaN(deadline) || deadline < Date.now()) {
      deadline = Date.now() + minutes * 60 * 1000;
      try {
        sessionStorage.setItem(TIMER_STORAGE_KEY, String(deadline));
      } catch (e) {
        /* navigation privée : on continue sans persistance */
      }
    }

    if (timerInterval) clearInterval(timerInterval);

    function tick() {
      var target = document.querySelector('[data-cart-timer]');
      if (!target) {
        clearInterval(timerInterval);
        timerInterval = null;
        return;
      }
      var remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
      var m = Math.floor(remaining / 60);
      var s = remaining % 60;
      target.textContent = m + ':' + (s < 10 ? '0' : '') + s;
      if (remaining === 0 && timerInterval) {
        clearInterval(timerInterval);
        timerInterval = null;
      }
    }

    tick();
    timerInterval = setInterval(tick, 1000);
  }

  /* ---------------------------------------------------------
     L'élément <cart-drawer>
     --------------------------------------------------------- */
  class BkCartDrawer extends HTMLElement {
    connectedCallback() {
      this.setHeaderCartIconAccessibility();
      startTimer();
      document.dispatchEvent(new CustomEvent('cart-drawer:connected'));
    }

    /* --- Accessibilité de l'icône panier du header (repris du thème) --- */
    setHeaderCartIconAccessibility() {
      var cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink || cartLink.dataset.bkcartBound) return;
      cartLink.dataset.bkcartBound = '1';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      var self = this;
      cartLink.addEventListener('keydown', function (event) {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          self.open(cartLink);
        }
      });
    }

    /* --- Ouverture / fermeture --- */
    open(triggeredBy) {
      // Tiroir neutralise (reglage « Tiroir de panier actif » decoche) : on ne
      // fait rien. Les autres methodes restent operationnelles, product-form.js
      // les appelle sans verifier.
      if (this.hasAttribute('data-bk-disabled')) return;

      if (triggeredBy) this.setActiveElement(triggeredBy);
      else this.activeElement = document.activeElement;

      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      void this.offsetWidth; // reflow, pour que la transition se joue
      this.classList.add('is-open');
      document.body.classList.add('bkcart-open');

      startTimer();

      var closeBtn = this.querySelector('[data-cart-close]');
      if (closeBtn && closeBtn.focus) closeBtn.focus();

      this.dispatchEvent(new CustomEvent('cart-drawer:open', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      document.dispatchEvent(new CustomEvent('bkcart:open'));
    }

    close() {
      var self = this;
      this.classList.remove('is-open');
      document.body.classList.remove('bkcart-open');
      this.setAttribute('aria-hidden', 'true');
      window.setTimeout(function () {
        if (!self.classList.contains('is-open')) self.hidden = true;
      }, 300);
      if (this.activeElement && this.activeElement.focus) this.activeElement.focus();
      document.dispatchEvent(new CustomEvent('cart-drawer:close'));
      document.dispatchEvent(new CustomEvent('bkcart:close'));
    }

    isOpen() {
      return this.classList.contains('is-open');
    }

    /* --- API de compatibilité thème --- */
    setActiveElement(element) {
      this.activeElement = element;
    }

    getSectionsToRender() {
      return [
        { id: SECTION_ID, selector: ROOT_SELECTOR },
        { id: BUBBLE_ID, selector: '.shopify-section' }
      ];
    }

    /* Applique les sections reçues SANS ouvrir le tiroir. */
    applySections(sections) {
      if (!sections) return false;
      var applied = false;

      if (sections[SECTION_ID]) {
        var parsed = new DOMParser().parseFromString(sections[SECTION_ID], 'text/html');
        var fresh = parsed.querySelector(ROOT_SELECTOR);
        if (fresh) {
          this.innerHTML = fresh.innerHTML;
          this.dataset.itemCount = fresh.dataset.itemCount || '0';
          if (fresh.dataset.timerMinutes) this.dataset.timerMinutes = fresh.dataset.timerMinutes;
          applied = true;
        }
      }

      this.renderCartIconBubble(sections[BUBBLE_ID]);
      startTimer();

      this.dispatchEvent(new CustomEvent('cart-drawer:update', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart-drawer:render'));
      document.dispatchEvent(new CustomEvent('bkcart:updated'));

      return applied;
    }

    /* Appelé par product-form.js / upsell-simple.js / shop-cart-button-handler.js
       après un ajout au panier : on rafraîchit ET on ouvre. */
    renderContents(parsedState) {
      if (!parsedState) return;
      var applied = this.applySections(parsedState.sections);
      if (!applied) {
        var self = this;
        return this.refreshCartDrawer().then(function () {
          self.open();
        });
      }
      this.open();
    }

    /* Rafraîchissement sans ouverture (bundle-and-save, upsell-simple). */
    refreshCartDrawer(sectionsData) {
      if (sectionsData && sectionsData[SECTION_ID]) {
        this.applySections(sectionsData);
        return Promise.resolve();
      }
      var self = this;
      return fetch(routesRoot() + '?sections=' + SECTION_ID + ',' + BUBBLE_ID)
        .then(function (r) { return r.json(); })
        .then(function (sections) { self.applySections(sections); })
        .catch(function (err) { console.error('[BkCart] refresh error', err); });
    }

    /* --- Pastille de comptage du header --- */
    renderCartIconBubble(html) {
      var target = document.getElementById('cart-icon-bubble');
      if (!target) return;

      var count = parseInt(this.dataset.itemCount, 10) || 0;

      if (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var section = doc.querySelector('.shopify-section');
        if (section) {
          var iconType = target.getAttribute('data-cart-icon-type');
          target.innerHTML = section.innerHTML;
          if (iconType) target.setAttribute('data-cart-icon-type', iconType);

          var wrapper = target.querySelector('.svg-wrapper');
          if (wrapper && iconType) {
            if (iconType === 'bag') {
              var bag = wrapper.querySelector('.cart-icon-bag');
              if (bag) bag.style.display = 'flex';
            } else {
              var full = wrapper.querySelector('.cart-icon-cart');
              var empty = wrapper.querySelector('.cart-icon-cart-empty');
              if (count === 0 && empty) empty.style.display = 'flex';
              else if (full) full.style.display = 'flex';
            }
          }
        }
      }

      // Filet de sécurité : si le thème n'a pas renvoyé la section, on met au
      // moins le chiffre à jour.
      target.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]').forEach(function (n) {
        n.textContent = count;
      });
      document.querySelectorAll('[data-cart-count]').forEach(function (n) {
        n.textContent = count;
      });
    }

    /* --- Mutations panier --- */
    setLoading(key, state) {
      var li = this.querySelector('.bkcart-item[data-line-key="' + CSS.escape(key) + '"]');
      if (li) li.classList.toggle('is-loading', !!state);
    }

    changeLine(key, quantity) {
      var self = this;
      this.setLoading(key, true);
      return postJSON(routesRoot() + 'cart/change.js', {
        id: key,
        quantity: quantity,
        sections: SECTION_ID + ',' + BUBBLE_ID,
        sections_url: window.location.pathname
      })
        .then(function (data) { self.applySections(data.sections); })
        .catch(function (err) {
          console.error('[BkCart] change error', err);
          self.setLoading(key, false);
          return self.refreshCartDrawer();
        });
    }

    addItem(payload) {
      var self = this;
      return postJSON(routesRoot() + 'cart/add.js', {
        items: [payload],
        sections: SECTION_ID + ',' + BUBBLE_ID,
        sections_url: window.location.pathname
      }).then(function (data) {
        self.applySections(data.sections);
        self.open();
        return data;
      });
    }

    /* Achat unique -> abonnement : Shopify n'autorise pas la mutation du
       selling plan sur une ligne existante, on retire puis on rajoute. */
    upgradeToSubscription(btn) {
      var self = this;
      var key = btn.dataset.lineKey;
      var variantId = parseInt(btn.dataset.variantId, 10);
      var sellingPlanId = parseInt(btn.dataset.sellingPlanId, 10);
      var quantity = parseInt(btn.dataset.quantity, 10) || 1;

      // le profil de poids doit survivre au remplacement de la ligne, sinon
      // la fréquence ne serait plus calculable ensuite
      var line = { id: variantId, quantity: quantity, selling_plan: sellingPlanId };
      if (btn.dataset.dogWeight) line.properties = { _poids: btn.dataset.dogWeight };

      this.setLoading(key, true);

      return postJSON(routesRoot() + 'cart/change.js', { id: key, quantity: 0 })
        .then(function () {
          return postJSON(routesRoot() + 'cart/add.js', {
            items: [line],
            sections: SECTION_ID + ',' + BUBBLE_ID,
            sections_url: window.location.pathname
          });
        })
        .then(function (data) { self.applySections(data.sections); })
        .catch(function (err) {
          console.error('[BkCart] upgrade error', err);
          return self.refreshCartDrawer();
        });
    }

  }

  if (!customElements.get('cart-drawer')) {
    customElements.define('cart-drawer', BkCartDrawer);
  }

  /* ---------------------------------------------------------
     Délégation d'événements (une seule fois, au niveau document)
     --------------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var drawer = root();

    // --- Fermeture ---
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      if (drawer) drawer.close();
      return;
    }

    // --- Ouverture : icône du header, lien /cart, ou opt-in explicite ---
    var opener = e.target.closest('[data-cart-open], #cart-icon-bubble, a[href$="/cart"]');
    if (opener && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
      if (drawer) {
        e.preventDefault();
        drawer.open(opener);
      }
      return;
    }

    if (!drawer) return;

    // --- Quantité ---
    var qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      e.preventDefault();
      var key = qtyBtn.dataset.lineKey;
      var input = drawer.querySelector('[data-cart-qty-input][data-line-key="' + CSS.escape(key) + '"]');
      var current = input ? parseInt(input.value, 10) || 1 : 1;
      var next = qtyBtn.dataset.cartQty === 'plus' ? current + 1 : current - 1;
      drawer.changeLine(key, Math.max(0, next));
      return;
    }

    // --- Suppression ---
    var removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      drawer.changeLine(removeBtn.dataset.lineKey, 0);
      return;
    }

    // --- Upsell abonnement ---
    var upgradeBtn = e.target.closest('[data-cart-upgrade]');
    if (upgradeBtn) {
      e.preventDefault();
      drawer.upgradeToSubscription(upgradeBtn);
    }
  });

  // Saisie manuelle de la quantité
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-cart-qty-input]');
    if (!input) return;
    var drawer = root();
    if (!drawer) return;
    var qty = parseInt(input.value, 10);
    if (isNaN(qty) || qty < 0) qty = 1;
    drawer.changeLine(input.dataset.lineKey, qty);
  });

  // Échap
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var drawer = root();
    if (drawer && drawer.isOpen && drawer.isOpen()) drawer.close();
  });

  /* ---------------------------------------------------------
     Hooks globaux attendus par le thème
     --------------------------------------------------------- */
  document.addEventListener('cart:ajax-update', function (event) {
    var drawer = root();
    if (!drawer) return;
    drawer.refreshCartDrawer((event.detail && event.detail.sectionsData) || null);
  });

  window.refreshCartDrawer = function (sectionsData) {
    var drawer = root();
    if (!drawer) return Promise.resolve();
    return drawer.refreshCartDrawer(sectionsData || null);
  };

  /* ---------------------------------------------------------
     API publique
     --------------------------------------------------------- */
  window.BkCart = {
    open: function (trigger) { var d = root(); if (d) d.open(trigger); },
    close: function () { var d = root(); if (d) d.close(); },
    refresh: function () { var d = root(); return d ? d.refreshCartDrawer() : Promise.resolve(); },
    addItem: function (payload) { var d = root(); return d ? d.addItem(payload) : Promise.reject(new Error('cart drawer absent')); }
  };
})();
